// A2F-766 follow-up: four-card Account page.
// A2F-767: Plan card wires to Stripe billing (Checkout + Customer Portal).
//
// Cards (in render order):
//   1. Account     — Full Name, Email (read-only), support footnote
//   2. Plan        — current plan + status pill, Upgrade/Manage CTA
//   3. Your data   — Sign out
//   4. Danger zone — Delete account (pure mailto, no backend call)
//
// Full Name comes from /api/v1/ribbon/session (Chrona backend),
// because Supabase JWT user_metadata doesn't carry it (public signup
// hits the Chrona /auth/signup endpoint, not Supabase directly). One
// extra round trip on Account mount; cheap on a low-traffic surface.

import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import * as api from '../lib/api'
import type { ProfileResponse, SessionResponse } from '../lib/api'
import { Nav } from '../components/Nav'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'

const SUPPORT_URL = 'https://chronabio.ai/support'
const DELETE_SUPPORT_EMAIL = 'support@chronabio.ai'

// "May 11, 2027" style — explicit month name, no ambiguity with
// MM/DD vs DD/MM across locales. Returns null for unset / invalid
// inputs so the JSX can render-or-skip without further null guards.
function formatBillingDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

type StripeError = { status?: number; body?: string }

function isStripeNotConfigured(err: unknown): boolean {
  // Backend returns 503 with detail.error === 'stripe_not_configured'
  // when STRIPE_SECRET_KEY is unset (local dev without billing).
  const e = err as StripeError | null
  if (!e || e.status !== 503) return false
  return typeof e.body === 'string' && e.body.includes('stripe_not_configured')
}

export function AccountPage() {
  const { session, loading: authLoading, signOut } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [sessionInfo, setSessionInfo] = useState<SessionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [signingOut, setSigningOut] = useState(false)
  const [billingPending, setBillingPending] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [billingNotConfigured, setBillingNotConfigured] = useState(false)

  // ?billing=success | canceled — set by Stripe redirects.
  const billingResult = searchParams.get('billing')

  useEffect(() => {
    if (authLoading) return
    if (!session) {
      setLoading(false)
      return
    }
    // Parallel fetch — both calls hit the authed backend, no ordering
    // dependency between them. allSettled means a fetchSession failure
    // doesn't blank the Plan card.
    Promise.allSettled([api.getProfile(), api.fetchSession()])
      .then(([p, s]) => {
        if (p.status === 'fulfilled') setProfile(p.value)
        if (s.status === 'fulfilled') setSessionInfo(s.value)
      })
      .finally(() => setLoading(false))
  }, [session, authLoading])

  // After a Stripe success redirect, the webhook may not have landed
  // by the time we re-mount — refetch a couple of times so the Plan
  // card flips to Pro without a full page reload.
  useEffect(() => {
    if (billingResult !== 'success' || !session) return
    let cancelled = false
    let attempts = 0
    const tick = async () => {
      while (!cancelled && attempts < 5) {
        attempts += 1
        try {
          const p = await api.getProfile()
          if (cancelled) return
          setProfile(p)
          if (p.subscription?.tier === 'pro') return
        } catch {
          // ignore — try again
        }
        await new Promise((r) => setTimeout(r, 1500))
      }
    }
    void tick()
    return () => {
      cancelled = true
    }
  }, [billingResult, session])

  async function handleUpgrade() {
    setBillingError(null)
    setBillingPending(true)
    try {
      // Risk #3 (A2F-767): a fast double-click here creates two
      // independent Checkout sessions. Stripe handles only one
      // success; the unused session expires server-side. Server
      // also blocks already-pro users via 400 already_pro. Not
      // worth UI plumbing beyond the disabled-while-pending state
      // below.
      const { checkoutUrl } = await api.createCheckoutSession()
      window.location.href = checkoutUrl
    } catch (err) {
      if (isStripeNotConfigured(err)) {
        setBillingNotConfigured(true)
      } else {
        setBillingError('Could not start checkout. Please try again.')
      }
      setBillingPending(false)
    }
  }

  async function handleManage() {
    setBillingError(null)
    setBillingPending(true)
    try {
      const { portalUrl } = await api.createPortalSession()
      window.location.href = portalUrl
    } catch (err) {
      if (isStripeNotConfigured(err)) {
        setBillingNotConfigured(true)
      } else {
        setBillingError('Could not open billing portal. Please try again.')
      }
      setBillingPending(false)
    }
  }

  function dismissBillingResult() {
    const next = new URLSearchParams(searchParams)
    next.delete('billing')
    next.delete('intent')
    setSearchParams(next, { replace: true })
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">
        <Loader2 className="animate-spin" size={28} />
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/configure" replace />
  }

  const email = session.user?.email ?? ''
  const fullName = sessionInfo?.displayName?.trim() || ''
  const sub = profile?.subscription
  const daysLeft =
    sub?.tier === 'trial' && sub.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(sub.trialEndsAt).getTime() - Date.now()) / 86_400_000))
      : null
  const tierBadgeText =
    sub?.tier === 'trial'
      ? `Trial · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
      : sub?.tier
        ? sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1)
        : 'Free'
  const showUpgrade = !sub || sub.tier === 'free' || sub.tier === 'trial'
  const showManage = sub?.tier === 'pro' && !!sub.stripeCustomerId
  const showPastDueBanner = sub?.tier === 'pro' && sub.status === 'past_due'

  // A2F-767: renewal metadata. cancelAt and currentPeriodEnd are
  // mutually exclusive for display — when a cancel is scheduled the
  // user wants to know the cutoff, not the would-be next renewal.
  const cancelAtDate = formatBillingDate(sub?.cancelAt)
  const renewsOnDate =
    !sub?.cancelAt && sub?.currentPeriodEnd
      ? formatBillingDate(sub.currentPeriodEnd)
      : null
  const memberSinceDate = formatBillingDate(sub?.convertedAt)

  // Pre-encode the email into the mailto body so a delete request lands
  // with the user's email already in the body — no copy/paste step.
  const deleteMailto =
    `mailto:${DELETE_SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent('Delete my account')}` +
    `&body=${encodeURIComponent(`Please delete my account. Email: ${email}`)}`

  async function handleSignOut() {
    setSigningOut(true)
    try {
      await signOut()
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-canvas text-brand-charcoal font-sans flex flex-col">
      <Nav />
      <main className="flex-1 mx-auto w-full max-w-2xl px-6 py-12 space-y-5">
        {/* Card 1 — Account */}
        <Card title="Account">
          <Row label="Full name">
            <span className="font-medium text-brand-charcoal">
              {fullName || <span className="text-slate-400">—</span>}
            </span>
          </Row>
          <Row label="Email" footnote={
            <>
              <a
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener"
                className="text-slate-500 hover:text-brand-orange underline-offset-2 hover:underline"
              >
                Contact support to change
              </a>
            </>
          }>
            <span className="font-medium text-brand-charcoal">{email}</span>
          </Row>
        </Card>

        {/* Card 2 — Plan */}
        <Card title="Plan">
          {billingResult === 'success' && (
            <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-3">
              <span>Welcome to Chrona Pro! Your subscription is active.</span>
              <button
                type="button"
                onClick={dismissBillingResult}
                className="text-emerald-700 hover:text-emerald-900 underline"
              >
                Dismiss
              </button>
            </div>
          )}
          {billingResult === 'canceled' && (
            <div className="mb-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 flex items-center justify-between gap-3">
              <span>Checkout canceled — you can try again any time.</span>
              <button
                type="button"
                onClick={dismissBillingResult}
                className="text-slate-600 hover:text-slate-900 underline"
              >
                Dismiss
              </button>
            </div>
          )}
          {showPastDueBanner && (
            <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              Payment failed — please update your payment method to keep
              Chrona Pro active.
            </div>
          )}
          <Row label="Current plan">
            <div className="flex items-center gap-3">
              <span className="font-medium text-brand-charcoal">Chrona Pro</span>
              <PlanBadge text={tierBadgeText} tier={sub?.tier} />
              {showUpgrade && !billingNotConfigured && (
                <button
                  type="button"
                  onClick={handleUpgrade}
                  disabled={billingPending}
                  className="text-xs font-medium text-brand-orange hover:underline disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {billingPending && <Loader2 size={11} className="animate-spin" />}
                  Upgrade →
                </button>
              )}
              {showManage && !billingNotConfigured && (
                <button
                  type="button"
                  onClick={handleManage}
                  disabled={billingPending}
                  className="text-xs font-medium text-slate-600 hover:text-brand-orange hover:underline disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {billingPending && <Loader2 size={11} className="animate-spin" />}
                  Manage subscription
                </button>
              )}
            </div>
          </Row>
          {/* A2F-767: renewal / cancellation date. cancelAt wins when
              set — "won't renew" framing makes the lifecycle explicit
              instead of leaving the user to infer it from the absence
              of a renewal date. */}
          {sub?.tier === 'pro' && cancelAtDate && (
            <Row label="Cancels on" hint="Won't auto-renew.">
              <span className="font-medium text-brand-charcoal">
                {cancelAtDate}
              </span>
            </Row>
          )}
          {sub?.tier === 'pro' && renewsOnDate && (
            <Row label="Renews on">
              <span className="font-medium text-brand-charcoal">
                {renewsOnDate}
              </span>
            </Row>
          )}
          {sub?.tier === 'pro' && memberSinceDate && (
            <Row label="Member since">
              <span className="text-slate-500">{memberSinceDate}</span>
            </Row>
          )}
          {billingError && (
            <div className="text-[11px] text-[#C84F1B] mt-2">{billingError}</div>
          )}
          {billingNotConfigured && (
            <div className="text-[11px] text-slate-400 mt-2">
              Billing isn&apos;t available in this environment.
            </div>
          )}
        </Card>

        {/* Card 3 — Your data */}
        <Card title="Your data">
          <Row label="Sign out" hint="Ends this browser session.">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-2"
            >
              {signingOut && <Loader2 size={12} className="animate-spin" />}
              Sign out
            </button>
          </Row>
        </Card>

        {/* Card 4 — Danger zone */}
        <Card title="Danger zone" tone="danger">
          <Row
            label="Delete account"
            hint="Opens a pre-filled email to support. We confirm before any deletion."
          >
            <a
              href={deleteMailto}
              className="rounded-md border border-[#F0997B] px-3 py-1.5 text-xs font-medium text-[#C84F1B] hover:bg-[#FFF5F0]"
            >
              Delete account
            </a>
          </Row>
        </Card>
      </main>
      <TermsPrivacyFooter />
    </div>
  )
}

// ── Card primitives ──────────────────────────────────────────────────

interface CardProps {
  title: string
  tone?: 'default' | 'danger'
  children: React.ReactNode
}

function Card({ title, tone = 'default', children }: CardProps) {
  // Coral border on the Danger card visually distances it from the
  // other three. Background stays white — colored backgrounds tend to
  // read as "this is broken" rather than "this is destructive".
  const borderClass =
    tone === 'danger' ? 'border-[#F0997B]' : 'border-slate-200'
  return (
    <section
      className={`bg-white rounded-xl border ${borderClass} px-6 py-5 shadow-[0_1px_3px_rgba(34,22,16,0.04)]`}
    >
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-3">
        {title}
      </h2>
      <div className="divide-y divide-slate-100">{children}</div>
    </section>
  )
}

interface RowProps {
  label: string
  hint?: string
  footnote?: React.ReactNode
  children: React.ReactNode
}

function Row({ label, hint, footnote, children }: RowProps) {
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3 text-sm">
        <div className="min-w-0">
          <div className="text-slate-500">{label}</div>
          {hint ? (
            <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
          ) : null}
        </div>
        <div className="shrink-0 text-right">{children}</div>
      </div>
      {footnote ? (
        <div className="text-[11px] text-slate-400 mt-1.5">{footnote}</div>
      ) : null}
    </div>
  )
}

// Plan-status pill — matches the v2 SectionCard statusOverride aesthetic.
function PlanBadge({ text, tier }: { text: string; tier?: string }) {
  // Trial → orange (live, time-bounded), Pro → emerald (active paid),
  // anything else (free, expired, undefined) → neutral.
  const cls =
    tier === 'trial'
      ? 'bg-brand-orange/10 text-brand-orange'
      : tier === 'pro' || tier === 'enterprise'
        ? 'bg-emerald-50 text-emerald-700'
        : 'bg-slate-100 text-slate-600'
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${cls}`}
    >
      {text}
    </span>
  )
}
